import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { RuleEditor } from './PayoutRuleSettingsPage.tsx';
import { type PayoutRule, type TournamentRuleType, validPayoutRule } from './payoutRule.ts';

interface TournamentRuleResponse {
  id: string;
  name: string;
  status: string;
  type: TournamentRuleType;
}

interface EffectiveRule {
  version: number;
  source: string;
  rule: PayoutRule;
}

export default function TournamentPayoutRulePage() {
  const { id } = useParams<{ id: string }>();
  return <TournamentPayoutRuleForm key={id} />;
}
function TournamentPayoutRuleForm() {
  const { t } = useTranslation('tournament');
  const [dirty, setDirty] = useState(false);
  const [draftVersion, setDraftVersion] = useState(0);
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PayoutRule | null>(null);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const { data, error } = useQuery({
    queryKey: ['tournament', id],
    queryFn: () => api.get<TournamentRuleResponse>(`/tournament/${id}`),
    enabled: !!id,
  });
  const { data: effectiveRule, error: ruleError } = useQuery({
    queryKey: ['tournament-payout-rule', id],
    queryFn: () => api.get<EffectiveRule>(`/tournament/${id}/payout-rule`),
    enabled: !!id,
  });
  useEffect(() => {
    if (effectiveRule && !dirty) {
      setDraft(structuredClone(effectiveRule.rule));
      setDraftVersion(effectiveRule.version);
    }
  }, [effectiveRule, dirty]);
  const update = useMutation({
    mutationFn: () =>
      api.put(`/tournament/${id}/payout-rule`, {
        expectedVersion: draftVersion,
        reason: reason.trim(),
        rule: draft,
      }),
    onSuccess: async () => {
      setMessage(t('payoutUi.eventSaved'));
      setDirty(false);
      setReason('');
      await qc.invalidateQueries({ queryKey: ['tournament', id] });
      await qc.invalidateQueries({ queryKey: ['tournament-payout-rule', id] });
    },
    onError: (failure: unknown) =>
      setMessage(
        t(
          (failure as { response?: { status?: number } }).response?.status === 409
            ? 'payoutUi.conflict'
            : 'payoutUi.failed',
        ),
      ),
  });
  const locked = data?.status === 'COMPLETED' || data?.status === 'CANCELLED';
  return (
    <AdminPage
      contentClassName="admin-content-form space-y-5"
      header={
        <AdminPageHeader
          title={t('payoutUi.eventTitle')}
          back={
            <Link
              to={`/tournament/${id}`}
              className="inline-flex min-h-11 items-center text-info text-sm"
            >
              {t('payoutUi.back')}
            </Link>
          }
        />
      }
    >
      <p>{data?.name}</p>
      {error && (
        <p role="alert" className="text-error">
          {t('payoutUi.failed')}
        </p>
      )}
      {ruleError && (
        <p role="alert" className="text-error">
          {t('payoutUi.failed')}
        </p>
      )}
      {data && effectiveRule && draft && (
        <section className="space-y-4 rounded-xl border p-4">
          <p className="text-sm">
            {t(
              effectiveRule.source === 'EVENT_OVERRIDE' ? 'payoutUi.override' : 'payoutUi.snapshot',
            )}
          </p>
          <RuleEditor
            type={data.type}
            value={draft}
            onChange={(value) => {
              setDirty(true);
              setDraft(value);
            }}
          />
          <label className="block text-sm">
            {t('payoutUi.reason')}
            <input
              className="mt-1 w-full rounded border p-2 min-h-11 text-base"
              value={reason}
              onChange={(event) => {
                setDirty(true);
                setReason(event.target.value);
              }}
            />
          </label>
          {locked && <p className="text-warning text-sm">{t('payoutUi.locked')}</p>}
          {!validPayoutRule(draft, data?.type) && (
            <p className="text-error text-sm">{t('payoutUi.invalid')}</p>
          )}
          <button
            type="button"
            className="rounded bg-primary px-4 py-2 min-h-11 text-primary-content disabled:opacity-50"
            disabled={
              locked || !validPayoutRule(draft, data?.type) || !reason.trim() || update.isPending
            }
            onClick={() => update.mutate()}
          >
            {t('payoutUi.save')}
          </button>
          {message && (
            <p role="status" className="text-sm">
              {message}
            </p>
          )}
        </section>
      )}
    </AdminPage>
  );
}
